import { useState, useEffect, useCallback, useRef } from "react";
import EmailCaptureForm from "./EmailCaptureForm";

import { useTranslation } from 'react-i18next';
const SESSION_KEY = "maxemployee.exit_intent_shown";

export default function ExitIntentPopup() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [closing, setClosing] = useState(false);
  const scrollPercentRef = useRef(0);

  const closePopup = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 150);
  }, []);

  useEffect(() => {
    // Check sessionStorage — only show once per session
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // Mobile: show when user scrolls past 50% then scrolls back up
      const handleScroll = () => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;
        const percent = scrollTop / docHeight;
        scrollPercentRef.current = percent;

        if (percent > 0.5) {
          // User scrolled past 50%, watch for scroll up
          let lastScroll = scrollTop;
          const checkScrollUp = () => {
            const currentScroll = window.scrollY;
            if (currentScroll < lastScroll - 50) {
              // Scrolled up significantly after being past 50%
              setVisible(true);
              setAnimating(true);
              sessionStorage.setItem(SESSION_KEY, "true");
              window.removeEventListener("scroll", checkScrollUp);
            }
            lastScroll = currentScroll;
          };
          window.addEventListener("scroll", checkScrollUp, { passive: true });
          window.removeEventListener("scroll", handleScroll);
        }
      };
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    } else {
      // Desktop: exit-intent (mouse leaves viewport upward)
      const handleMouseLeave = (e) => {
        if (e.clientY <= 0 && !e.relatedTarget) {
          setVisible(true);
          setAnimating(true);
          sessionStorage.setItem(SESSION_KEY, "true");
          document.removeEventListener("mouseleave", handleMouseLeave);
        }
      };
      document.documentElement.addEventListener("mouseleave", handleMouseLeave);
      return () => document.documentElement.removeEventListener("mouseleave", handleMouseLeave);
    }
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closePopup();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [visible, closePopup]);

  // Animation: remove animating class after entry
  useEffect(() => {
    if (animating) {
      const timer = setTimeout(() => setAnimating(false), 200);
      return () => clearTimeout(timer);
    }
  }, [animating]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      role="presentation"
    >
      {/* Overlay — click to close */}
      <div
        className={`absolute inset-0 bg-black/70 transition-opacity duration-200 ${
          closing ? "opacity-0" : "opacity-100"
        }`}
        onClick={closePopup}
        aria-hidden="true"
      />

      {/* Popup card */}
      <div
        className={`relative z-10 transition-all duration-200 ${
          closing
            ? "opacity-0 scale-95"
            : animating
            ? "opacity-0 scale-95"
            : "opacity-100 scale-100"
        }`}
        style={{ transitionDuration: closing ? "150ms" : "200ms" }}
      >
        <EmailCaptureForm onClose={closePopup} />
      </div>
    </div>
  );
}
